import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Container,
  Grid as GridBase,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import SwitchTabs from "@/components/tabs/SwitchTabs";
import RequestTypeSelector from "./RequestTypeSelector";
import RequestListing from "./RequestListing";
import { logger } from "@/helpers/loggerHelper";
import DynamicFormBase from "./DynamicForm";
import { requestService } from "../services/requestService";
import { toastr } from "react-redux-toastr";
import { GlobalContext, LoaderContext } from "@/helpers/contexts/contexts";
import { BRAND_REQUEST_TYPE, HTTP_STATUS } from "@/config/constants";
import { BRAND_REQUEST_TYPES } from "../utils/constants";

type ContextId = string | number | undefined;

type SelectedBrand = {
  id?: ContextId;
};

type LoggedInUser = {
  id?: ContextId;
};

type ApiResponse<TData> = {
  status?: number;
  data?: TData;
};

type CampaignListingResponse = {
  result?: unknown[];
};

type BrandRequestSettings = {
  channels?: unknown[];
  campaigns?: unknown[];
  groups?: unknown[];
  campaignGoals?: unknown[];
  locationRes?: ApiResponse<unknown[]>;
};

const Grid = GridBase as React.ComponentType<Record<string, unknown>>;
const DynamicForm = DynamicFormBase as React.ComponentType<
  Record<string, unknown>
>;

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2), // Default padding
  margin: theme.spacing(1), // Default padding
  [theme.breakpoints.down("sm")]: {
    padding: 0, // No padding for small screens (mobile view)
  },
}));

const RequestFromBrand = () => {
  const loader = useContext(LoaderContext);
  const [selectedRequestType, setSelectedRequestType] = useState("");
  const { selectedBrand, loggedInUser } = useContext(GlobalContext);
  const brand = selectedBrand as SelectedBrand | null | undefined;
  const user = loggedInUser as LoggedInUser | null | undefined;
  const [activeTab, setActiveTab] = useState(0);

  const [settings, setSettings] = useState<BrandRequestSettings | null>(null);

  useEffect(() => {
    setSelectedRequestType("");
  }, [activeTab]);

  const fetchSettings = useCallback(async () => {
    try {
      loader(true);
      const [
        channelRes,
        campaignRes,
        groupsRes,
        campaignGoalsRes,
        locationRes,
      ] = await Promise.all([
        requestService.getChannel(),
        requestService.getCampaignListing({
          id: brand?.id,
          userId: user?.id,
        }),
        requestService.getGroupsBrandRequest({
          fk_brand_id: brand?.id,
        }),
        requestService.getCampaignGoals(),
        selectedRequestType !== BRAND_REQUEST_TYPE.NEW_CAMPAIGN
          ? requestService.getBrandLocations({ id: brand?.id })
          : null,
      ]);

      const channels = channelRes.data || [];
      const campaigns =
        (campaignRes.data as CampaignListingResponse | undefined)?.result || [];
      const groups = groupsRes.data || [];
      const campaignGoals = campaignGoalsRes.data || [];
      if (channelRes.status === HTTP_STATUS.OK) {
        const data = Object.assign(
          {},
          channels ? { channels } : {},
          campaigns ? { campaigns } : {},
          groups ? { groups } : {},
          campaignGoals ? { campaignGoals } : {},
          locationRes ? { locationRes } : {}
        );
        setSettings(Object.keys(data) ? data : null);
      } else {
        toastr.error(
          "Error",
          "Something went wrong while fetching the settings!"
        );
      }
    } catch (err) {
      logger.trace(err);
    } finally {
      loader(false);
    }
  }, [loader, selectedRequestType, brand?.id, user?.id]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleRequestTypeChange = useCallback((type: string) => {
    setSelectedRequestType(type);
  }, []);

  const handleOnSuccess = useCallback(() => {
    // setSelectedRequestType("");
  }, []);

  const tabs = ["Submit Request", "Request History"];

  return (
    <Container className="px-0 px-lg-auto">
      <Grid alignItems="center">
        <Grid item>
          <div className="mb-4 d-flex justify-content-center">
            <SwitchTabs
              tabs={tabs}
              selectedTab={activeTab}
              onChange={setActiveTab}
              // centered
              tabProps={{
                disableRipple: true,
              }}
              styleType={1}
            />
          </div>
          <Card elevation={0} className="shadow-sm">
            <StyledCardContent
              className={`${activeTab === 1 ? "px-0 mx-0" : ""}`}
            >
              {activeTab === 0 && (
                <>
                  <Typography
                    variant="h4"
                    className="text-center mb-4"
                    gutterBottom
                  >
                    Submit Request
                  </Typography>
                  <div className="mb-3 p-0">
                    <RequestTypeSelector
                      onChange={handleRequestTypeChange}
                      selectedRequestType={selectedRequestType}
                      brandRequestList={BRAND_REQUEST_TYPES}
                    />
                  </div>
                  {selectedRequestType && (
                    <DynamicForm
                      requestType={selectedRequestType}
                      channels={settings?.channels}
                      brandRequestList={BRAND_REQUEST_TYPES}
                      locations={settings?.locationRes?.data || []}
                      groups={settings?.groups}
                      setActiveTab={setActiveTab}
                      onSuccess={handleOnSuccess}
                      campaigns={settings?.campaigns}
                      campaignGoals={settings?.campaignGoals}
                    />
                  )}
                </>
              )}
              {activeTab === 1 && (
                <div>
                  <Typography
                    variant="h4"
                    className="text-center mb-4"
                    gutterBottom
                  >
                    Request History
                  </Typography>
                  <RequestListing />
                </div>
              )}
            </StyledCardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RequestFromBrand;
